import { useState, useEffect, useRef } from "react";
import { cn } from "../lib/utils";

interface CountUpProps {
  to: number;
  from?: number;
  duration?: number;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  separator?: string;
  className?: string;
  start?: boolean;
  onComplete?: () => void;
}

function easeOutExpo(t: number): number {
  return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
}

export function CountUp({
  to,
  from = 0,
  duration = 2000,
  decimals = 0,
  prefix = "",
  suffix = "",
  separator = ",",
  className,
  start = true,
  onComplete,
}: CountUpProps) {
  const [value, setValue] = useState(from);
  const startTime = useRef<number | null>(null);
  const rafId = useRef<number>();

  useEffect(() => {
    if (!start) {
      setValue(from);
      return;
    }
    startTime.current = null;

    const tick = (now: number) => {
      if (startTime.current === null) startTime.current = now;
      const elapsed = now - startTime.current;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeOutExpo(progress);
      setValue(from + (to - from) * eased);
      if (progress < 1) {
        rafId.current = requestAnimationFrame(tick);
      } else {
        setValue(to);
        onComplete?.();
      }
    };

    rafId.current = requestAnimationFrame(tick);
    return () => { if (rafId.current) cancelAnimationFrame(rafId.current); };
  }, [to, from, duration, start]);

  const formatted = value
    .toFixed(decimals)
    .replace(/\B(?=(\d{3})+(?!\d))/g, separator);

  return (
    <span className={cn("count-up", className)}>
      {prefix}{formatted}{suffix}
    </span>
  );
}
